import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, Dog, Clock, Phone, Mail, MessageCircle, Lock, Filter, Send, DollarSign, CheckCircle, XCircle, ChevronDown, ChevronUp, Shield, Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import PremiumUpsellModal from './PremiumUpsellModal';
import { isUcanApp, isApplePlatform } from '@/lib/ucanApp';

const SERVICE_LABELS = {
  paseo: 'Paseo',
  cuidado: 'Cuidado',
  daycare: 'Daycare'
};

const PET_SIZE_LABELS = {
  pequeno: 'Pequeno',
  mediano: 'Mediano',
  grande: 'Grande'
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProposalForm = ({ requestId, onSubmit, onCancel, hasSubscription }) => {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const defaultMessage = 'Estoy interesado en tu solicitud.';

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalMessage = hasSubscription && message.trim()
      ? message.trim()
      : defaultMessage;

    setSubmitting(true);
    try {
      const res = await api.post('/proposals', {
        care_request_id: requestId,
        price: 0,
        message: finalMessage,
        available_dates: [],
        application_type: 'quick_apply'
      });
      toast.success('Postulación enviada. Te avisaremos si el cliente te selecciona.');
      onSubmit(res.data);
    } catch (error) {

      if (error.response?.data?.detail === 'FREE_MONTH_LIMIT') {
        setShowPremiumModal(true);
        return;
      }

      toast.error(error.response?.data?.detail || 'Error al postular');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PremiumUpsellModal
        open={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
      />


    <form id={`proposal-form-${requestId}`} onSubmit={handleSubmit} className="mt-3 p-4 bg-red-50 rounded-xl border border-[#E6202E]/30 space-y-3 shadow-sm" data-testid={`proposal-form-${requestId}`}>
      <div>
        <p className="text-sm font-bold text-gray-900">¿Quieres postular a esta solicitud?</p>
        <p className="text-xs text-gray-500 mt-1">
          El cliente recibirá a los cuidadores interesados y podrá elegir con quién conversar.
        </p>
      </div>

      {hasSubscription ? (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Mensaje opcional Premium
          </label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value.slice(0, 150))}
            placeholder="Ej: Estoy disponible esos días y tengo experiencia con perros grandes."
            rows={3}
            maxLength={150}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#E6202E] focus:outline-none text-sm resize-none"
            data-testid="proposal-message-input"
          />
          <p className="text-[11px] text-gray-400 text-right mt-1">{message.length}/150</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border p-3 text-xs text-gray-600">
          Los cuidadores Premium pueden agregar un mensaje corto para destacar su postulación.
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} data-testid="proposal-cancel-btn">
          Cancelar
        </Button>
        <Button type="submit" size="sm" disabled={submitting} className="bg-[#E6202E] hover:bg-[#D31522]" data-testid="proposal-submit-btn">
          {submitting ? 'Postulando...' : <><Send className="w-3.5 h-3.5 mr-1" />Quiero postular</>}
        </Button>
      </div>
    </form>
    </>
  );
};

const CareRequestsProvider = ({ hasSubscription = false, onCountChange }) => {
  const [requests, setRequests] = useState([]);
  const [sentProposals, setSentProposals] = useState({});
  const [loading, setLoading] = useState(true);
  const [serviceFilter, setServiceFilter] = useState('');
  const [comunaFilter, setComunaFilter] = useState('');
  const [expandedForm, setExpandedForm] = useState(null);
  const [freeProposalMode, setFreeProposalMode] = useState(false);
  const [providerPremiumPlan, setProviderPremiumPlan] = useState(null);
  const [startingPremiumCheckout, setStartingPremiumCheckout] = useState(false);

  useEffect(() => {
    loadRequests();
    loadSentProposals();

    if (!hasSubscription) {
      api.get('/subscription/plans')
        .then(res => {
          const providerPlan =
            res.data.find(p => p.user_type === 'provider') ||
            res.data.find(p => p.user_type === 'cuidador') ||
            res.data.find(p => String(p.name || '').toLowerCase().includes('cuidador')) ||
            res.data[0];

          setProviderPremiumPlan(providerPlan || null);
        })
        .catch(() => {});
    }
  }, [serviceFilter, hasSubscription]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (serviceFilter && serviceFilter !== 'all') params.set('service_type', serviceFilter);
      if (comunaFilter) params.set('comuna', comunaFilter);
      
      const res = await api.get(`/care-requests?${params.toString()}`);
      setRequests(res.data);
      if (onCountChange) onCountChange(res.data.length);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Solo cuidadores pueden ver solicitudes');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSentProposals = async () => {
    try {
      const res = await api.get('/proposals/my-sent');
      const map = {};
      res.data.forEach(p => { map[p.care_request_id] = p; });
      setSentProposals(map);
    } catch {}
  };

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadRequests();
  };

  const handleProposalSent = (proposal) => {
    setSentProposals(prev => ({ ...prev, [proposal.care_request_id]: proposal }));
    setExpandedForm(null);
    setFreeProposalMode(false);
  };

  const now = new Date();

  const usedFreeProposalThisMonth = !hasSubscription && Object.values(sentProposals).some(proposal => {
    if (!proposal?.created_at) return false;
    const created = new Date(proposal.created_at);

    return (
      created.getFullYear() === now.getFullYear() &&
      created.getMonth() === now.getMonth()
    );
  });

  const canUseFreeProposal =
    !hasSubscription &&
    !usedFreeProposalThisMonth;

  const handlePremiumCheckout = async () => {
    if (startingPremiumCheckout) return;

    if (!providerPremiumPlan?.plan_id) {
      toast.error('No se pudo cargar el plan Premium de cuidador');
      return;
    }

    // Dentro de la app iOS Premium se compra exclusivamente
    // mediante Apple In-App Purchase.
    if (isUcanApp()) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          type: 'APPLE_SUBSCRIPTION',
          plan_id: providerPremiumPlan.plan_id,
          user_type: 'provider'
        })
      );
      return;
    }

    // Cualquier navegador en plataforma Apple:
    // nunca Mercado Pago. La suscripción Apple se completa
    // desde la app U-CAN para iOS.
    if (isApplePlatform()) {
      toast.info('Suscríbete con Apple desde la app U-CAN para iOS.');
      return;
    }

    // Android / Windows / Linux web: checkout externo.
    setStartingPremiumCheckout(true);

    try {
      const response = await api.post('/subscription/create-payment', {
        plan_id: providerPremiumPlan.plan_id
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }

      if (response.data.sandbox_url) {
        window.location.href = response.data.sandbox_url;
        return;
      }

      toast.error('No se pudo iniciar el pago');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar Mercado Pago');
    } finally {
      setStartingPremiumCheckout(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8" data-testid="care-requests-provider-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold text-xl flex items-center gap-2">
          <Dog className="w-5 h-5 text-[#E6202E]" />
          Clientes Buscando Cuidador
          <span className="text-sm font-normal text-gray-500">({requests.length})</span>
        </h2>
      </div>

      {!hasSubscription && (
        <div className="mb-6 space-y-4">

          {!usedFreeProposalThisMonth ? (
            <div className="rounded-3xl border-2 border-red-200 bg-gradient-to-br from-red-50 to-white p-6 shadow-sm">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#E6202E] flex items-center justify-center shrink-0 shadow-sm">
                    <Send className="w-7 h-7 text-white" />
                  </div>

                  <div>
                    <p className="text-2xl font-black text-gray-900">
                      Tienes 1 propuesta GRATIS este mes
                    </p>

                    <p className="text-sm text-gray-600 mt-2 max-w-2xl">
                      Activa tu oportunidad gratis, revisa las solicitudes disponibles y elige una para enviar tu propuesta.
                    </p>

                    {freeProposalMode && (
                      <div className="mt-3 inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-bold">
                        <CheckCircle className="w-4 h-4" />
                        Oportunidad gratis activa · Elige una solicitud
                      </div>
                    )}
                  </div>
                </div>

                {!freeProposalMode && (
                  <Button
                    onClick={() => setFreeProposalMode(true)}
                    className="w-full lg:w-auto bg-[#E6202E] hover:bg-[#D31522] text-white font-black text-base rounded-2xl px-7 py-7 shadow-md"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Activar mi propuesta gratis
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-3xl border-2 border-gray-200 bg-gray-50 p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gray-200 flex items-center justify-center shrink-0">
                  <Lock className="w-7 h-7 text-gray-500" />
                </div>

                <div>
                  <p className="text-2xl font-black text-gray-900">
                    Ya utilizaste tu propuesta gratis de este mes
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Tu próxima oportunidad gratis estará disponible el próximo mes. Activa Premium para seguir postulando ahora.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-3xl border-2 border-green-200 bg-gradient-to-br from-green-50 to-white p-6 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">

              <div>
                <p className="text-xl font-black text-gray-900">
                  ¿Quieres enviar propuestas sin límites?
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Activa U-CAN Premium y postula a todas las solicitudes disponibles.
                </p>
              </div>

              <Button
                onClick={handlePremiumCheckout}
                disabled={startingPremiumCheckout}
                className="w-full md:w-auto bg-green-600 hover:bg-green-700 text-white font-black rounded-2xl px-7 py-7 shadow-md"
              >
                {startingPremiumCheckout ? 'Redirigiendo...' : 'Activar U-CAN Premium'}
              </Button>

            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <Select value={serviceFilter} onValueChange={setServiceFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Servicio" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="paseo">Paseo</SelectItem>
            <SelectItem value="cuidado">Cuidado</SelectItem>
            <SelectItem value="daycare">Daycare</SelectItem>
          </SelectContent>
        </Select>
        <input
          type="text"
          value={comunaFilter}
          onChange={e => setComunaFilter(e.target.value)}
          placeholder="Filtrar por comuna"
          className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#E6202E] focus:outline-none text-sm"
        />
        <Button type="submit" variant="outline" size="sm">
          <Filter className="w-4 h-4" />
        </Button>
      </form>

      {requests.length === 0 ? (
        <div className="text-center py-8">
          <Dog className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500">No hay solicitudes de cuidado en este momento</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map(req => {
            const existingProposal = sentProposals[req.request_id];
            return (
              <div 
                key={req.request_id} 
                className="p-4 rounded-xl border bg-gray-50 hover:bg-gray-100 transition-colors"
                data-testid={`provider-care-request-${req.request_id}`}
              >
                <div className="flex gap-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-200 flex-shrink-0">
                    {req.pet_photo ? (
                      <img src={getPhotoUrl(req.pet_photo)} alt={req.pet_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Dog className="w-8 h-8 text-gray-400" /></div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-[#E6202E] rounded-full capitalize">
                        {SERVICE_LABELS[req.service_type] || req.service_type}
                      </span>
                      <span className="text-xs text-gray-500">
                        {req.pet_species} {req.pet_size ? `• ${PET_SIZE_LABELS[req.pet_size] || req.pet_size}` : ''}
                      </span>
                      {req.flexible_dates && (
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">Fechas flexibles</span>
                      )}
                      {req.proposal_count > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                          {req.proposal_count} propuesta{req.proposal_count > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-gray-900">{req.pet_name}</p>
                      {req.pet_breed && <span className="text-sm text-gray-500">({req.pet_breed})</span>}
                    </div>
                    <p className="text-sm text-gray-700 mb-2 line-clamp-2">{req.description}</p>

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{req.comuna}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(req.created_at).toLocaleDateString('es-CL')}</span>
                    </div>

                    {/* Client info + actions */}
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          {req.client_picture && !req.contact_hidden ? (
                            <img src={getPhotoUrl(req.client_picture)} alt={req.client_name} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-500">
                              {req.client_name?.[0] || 'C'}
                            </div>
                          )}
                          <span className="font-medium text-sm">
                            {req.client_name}
                            {req.contact_hidden && <span className="text-gray-400 ml-1">(nombre parcial)</span>}
                          </span>
                        </div>

                        {req.contact_hidden && !freeProposalMode ? (
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-gray-400" />
                            <span className="text-xs font-semibold text-gray-400">
                              {!canUseFreeProposal
                                ? 'Propuestas bloqueadas'
                                : 'Activa tu propuesta gratis arriba'}
                            </span>
                          </div>
                        ) : (
                          <div className="flex gap-2 flex-wrap">
                            {req.client_phone && (
                              <a href={`tel:${req.client_phone}`}>
                                <Button size="sm" variant="outline" className="text-xs"><Phone className="w-3 h-3 mr-1" />Llamar</Button>
                              </a>
                            )}
                            <Link to={`/chat?user=${req.client_id}`}>
                              <Button size="sm" variant="outline" className="text-xs"><MessageCircle className="w-3 h-3 mr-1" />Chat</Button>
                            </Link>
                            {existingProposal ? (
                              <div className="flex gap-2 flex-wrap" data-testid={`proposal-sent-${req.request_id}`}>
                                {existingProposal.status === 'accepted' ? (
                                  <>
                                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-green-100 text-green-800">
                                      <CheckCircle className="w-3.5 h-3.5" />
                                      Propuesta aceptada · Servicio en curso
                                    </div>

                                    <Link to="/historial?tab=en-curso">
                                      <Button size="sm" className="text-xs bg-green-600 hover:bg-green-700">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Ver servicio
                                      </Button>
                                    </Link>

                                    <Link to={`/chat?user=${req.client_id}`}>
                                      <Button size="sm" variant="outline" className="text-xs">
                                        <MessageCircle className="w-3 h-3 mr-1" />
                                        Abrir chat
                                      </Button>
                                    </Link>
                                  </>
                                ) : existingProposal.status === 'rejected' ? (
                                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600">
                                    <XCircle className="w-3.5 h-3.5" />
                                    Esta vez eligieron otra propuesta
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-100 text-blue-700">
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    Propuesta enviada · Esperando respuesta
                                  </div>
                                )}
                              </div>
) : (
  <Button
    size="sm"
    className={`text-xs ${expandedForm === req.request_id ? 'bg-gray-500 hover:bg-gray-600' : 'bg-[#E6202E] hover:bg-[#D31522]'}`}
    onClick={() => {
      const newValue = expandedForm === req.request_id ? null : req.request_id;
      setExpandedForm(newValue);
      if (newValue) {
        setTimeout(() => {
          const el = document.getElementById(`proposal-form-${req.request_id}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }}
    data-testid={`send-proposal-btn-${req.request_id}`}
  >
    {expandedForm === req.request_id ? (
      <><X className="w-3 h-3 mr-1" />Cerrar</>
    ) : (
      <><Send className="w-3 h-3 mr-1" />Enviar Propuesta</>
    )}
  </Button>
)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Inline proposal form */}
                    {expandedForm === req.request_id && !existingProposal && (
                      <ProposalForm
                        requestId={req.request_id}
                        onSubmit={handleProposalSent}
                        onCancel={() => setExpandedForm(null)}
                        hasSubscription={hasSubscription}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CareRequestsProvider;
